import { type CapturedMedia, generateFilename } from "./utils";

export type PhotoErrorKind = "context_failed" | "size_exceeded" | "blob_failed";

export interface PhotoError {
  kind: PhotoErrorKind;
  message: string;
}

export type PhotoMimeType = "image/jpeg" | "image/png";

export interface CapturePhotoOptions {
  maxFileSize: number;
  /** Quality 0–1. Defaults to 0.92. Ignored for PNG (use JPEG for size control). */
  quality?: number;
  mimeType?: PhotoMimeType;
}

const DEFAULT_QUALITY = 0.92;
const DEFAULT_MIME_TYPE: PhotoMimeType = "image/jpeg";


/**
 * Captures a JPEG snapshot from a `<video>` element via an offscreen canvas.
 *
 * Returns `{ ok: true, media }` on success or `{ ok: false, error }` on
 * failure — never throws. The caller is responsible for revoking the returned
 * blob URL via `URL.revokeObjectURL(media.url)` when it is no longer needed.
 */
export async function capturePhoto(
  videoEl: HTMLVideoElement,
  opts: CapturePhotoOptions
): Promise<
  | { ok: true; media: CapturedMedia }
  | { ok: false; error: PhotoError }
> {
  const {
    maxFileSize,
    quality = DEFAULT_QUALITY,
    mimeType = DEFAULT_MIME_TYPE,
  } = opts;

  const canvas = document.createElement("canvas");
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;

  const ctx = canvas.getContext("2d", {
    alpha: false,
    willReadFrequently: false,
  });

  if (ctx == null) {
    return {
      ok: false,
      error: {
        kind: "context_failed",
        message: "Failed to initialise camera capture. Please try again.",
      },
    };
  }

  ctx.drawImage(videoEl, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob == null) {
          resolve({
            ok: false,
            error: {
              kind: "blob_failed",
              message: "Failed to capture photo. Please try again.",
            },
          });
          return;
        }

        if (blob.size > maxFileSize) {
          resolve({
            ok: false,
            error: {
              kind: "size_exceeded",
              message: `Photo exceeds the maximum size of ${Math.round(maxFileSize / 1024 / 1024)} MB.`,
            },
          });
          return;
        }

        const extension = mimeType === "image/jpeg" ? "jpg" : "png";
        const file = new File([blob], generateFilename("photo", extension), {
          type: mimeType,
        });
        const url = URL.createObjectURL(blob);

        resolve({ ok: true, media: { type: "photo", url, file } });
      },
      mimeType,
      quality
    );
  });
}
